<template>
  <header
    id="navbar"
    class="fixed bg-white inset-x-0 top-0 z-[60] w-full border-b border-transparent bg-white transition-all duration-300 dark:bg-default-50 [&amp;.nav-sticky]:bg-white/90 [&amp;.nav-sticky]:shadow-md [&amp;.nav-sticky]:backdrop-blur-3xl dark:[&amp;.nav-sticky]:bg-default-50/80"
  >
    <div
      class="flex h-full items-center py-4 w-full bg-white/80 backdrop-blur-sm border-b border-gray-100"
    >
      <div class="container">
        <nav
          class="flex flex-wrap items-center justify-between gap-4 lg:flex-nowrap"
          style="flex-flow: row"
        >
          <!-- Logo Section -->
          <div class="flex w-full items-center justify-between lg:w-auto">
            <a href="/" class="transition-transform duration-200 hover:scale-105">
              <img
                src="@/assets/img/logo-long.png"
                alt="logo"
                class="flex h-10 dark:hidden drop-shadow-sm"
              />
              <img
                src="@/assets/img/logo-long.png"
                alt="logo"
                class="hidden h-10 dark:flex drop-shadow-sm"
              />
            </a>

            <!-- Mobile Menu Button -->
            <div class="flex items-center gap-2">
              <button
                class="hs-collapse-toggle inline-block lg:hidden p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                data-hs-overlay="#mobile-menu"
                @click="toggleMobileMenu()"
                title="Menu"
              >
                <svg
                  stroke="currentColor"
                  fill="none"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="h-6 w-6 text-gray-600 hover:text-gray-900 transition-colors duration-200"
                  height="1em"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line x1="4" x2="20" y1="12" y2="12"></line>
                  <line x1="4" x2="20" y1="6" y2="6"></line>
                  <line x1="4" x2="20" y1="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>

          <!-- Main Menu -->
          <ul class="menu relative mx-auto hidden grow items-center justify-center lg:flex">
            <li
              v-for="item in menuItems"
              :key="item.name"
              :class="{ active: isLinkActive(item.url) }"
              class="menu-item mx-3"
            >
              <a
                class="relative inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium capitalize transition-all duration-200 text-gray-700 hover:text-primary-600 hover:bg-gray-50 lg:text-base"
                :href="`${item.url}`"
              >
                {{ item.name }}
                <span
                  class="absolute bottom-0 left-1/2 h-0.5 w-0 bg-primary-500 transition-all duration-300 hover:w-6 hover:-translate-x-1/2"
                ></span>
              </a>
            </li>
          </ul>

          <!-- Action Buttons -->
          <div class="ms-auto shrink gap-3 lg:inline-flex flex items-center">
            <!-- Connected User Button -->
            <a
              v-if="isLoggedIn"
              class="group inline-flex items-center gap-2 rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-primary-600 hover:shadow-lg hover:shadow-primary-500/25 lg:text-base"
              href="/dashboard"
            >
              <svg
                width="20px"
                height="20px"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="transition-transform duration-200 group-hover:scale-110"
              >
                <circle cx="12" cy="6" r="4" fill="currentColor"></circle>
                <ellipse opacity="0.7" cx="12" cy="17" rx="7" ry="4" fill="currentColor"></ellipse>
              </svg>
              <span class="hidden sm:block">Mon Espace</span>
            </a>

            <!-- Logout Button -->
            <a
              v-if="isLoggedIn"
              href="#"
              @click="logout()"
              title="Déconnexion"
              class="p-2 rounded-lg text-gray-600 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
            >
              <i class="fa-solid text-lg fa-right-from-bracket"></i>
            </a>

            <!-- Login Button -->
            <a
              v-if="!isLoggedIn"
              class="group inline-flex items-center gap-2 rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-primary-600 hover:shadow-lg hover:shadow-primary-500/25 lg:text-base"
              href="/login"
            >
              <svg
                width="20px"
                height="20px"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="transition-transform duration-200 group-hover:scale-110"
                aria-hidden="true"
              >
                <circle cx="12" cy="6" r="4" fill="currentColor"></circle>
                <ellipse opacity="0.7" cx="12" cy="17" rx="7" ry="4" fill="currentColor"></ellipse>
              </svg>
              <span class="hidden sm:block">Connexion / Inscription</span>
              <span class="sr-only">Connexion / Inscription</span>
            </a>
          </div>
        </nav>
      </div>
    </div>
    <nav
      v-if="
        isLoggedIn &&
        this.$route.path.includes('dashboard') &&
        !this.$route.path.includes('dashboard/exercises')
      "
      class="admin-menu border-b border-gray-300 bg-white text-sm font-medium shadow-sm shadow-default-100 dark:bg-default-50"
    >
      <div
        class="custom-scroll container mx-auto flex w-full snap-x items-center overflow-x-auto py-2.5"
      >
        <div class="shrink-0 snap-center px-2">
          <a
            class="inline-flex items-center gap-2 rounded-full px-3 py-2 font-semibold text-default-600 transition-all hover:bg-default-100 hover:text-default-800 [&amp;.active]:bg-primary/10 [&amp;.active]:text-primary"
            :class="{ active: $route.path == '/dashboard' }"
            href="/dashboard"
          >
            <svg
              width="24px"
              height="24px"
              viewBox="0 0 64 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
              <g id="SVGRepo_iconCarrier">
                <g clip-path="url(#clip0_14_1960)">
                  <path
                    d="M25.545 8H11C9.34315 8 8 9.34315 8 11V28.931C8 30.5879 9.34315 31.931 11 31.931H25.545C27.2019 31.931 28.545 30.5879 28.545 28.931V11C28.545 9.34315 27.2019 8 25.545 8Z"
                    stroke="#000000"
                    stroke-width="4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                  <path
                    d="M52.94 8H38.395C36.7382 8 35.395 9.34315 35.395 11V15.272C35.395 16.9289 36.7382 18.272 38.395 18.272H52.94C54.5969 18.272 55.94 16.9289 55.94 15.272V11C55.94 9.34315 54.5969 8 52.94 8Z"
                    stroke="#426AB2"
                    stroke-width="4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                  <path
                    d="M52.94 25.121H38.395C36.7382 25.121 35.395 26.4641 35.395 28.121V52.94C35.395 54.5969 36.7382 55.94 38.395 55.94H52.94C54.5969 55.94 55.94 54.5969 55.94 52.94V28.121C55.94 26.4641 54.5969 25.121 52.94 25.121Z"
                    stroke="#000000"
                    stroke-width="4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                  <path
                    d="M25.545 38.818H11C9.34315 38.818 8 40.1611 8 41.818V52.939C8 54.5958 9.34315 55.939 11 55.939H25.545C27.2019 55.939 28.545 54.5958 28.545 52.939V41.818C28.545 40.1611 27.2019 38.818 25.545 38.818Z"
                    stroke="#426AB2"
                    stroke-width="4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </g>
                <defs>
                  <clipPath id="clip0_14_1960">
                    <rect
                      width="51.94"
                      height="51.939"
                      fill="white"
                      transform="translate(6 6)"
                    ></rect>
                  </clipPath>
                </defs>
              </g>
            </svg>
            <span>Tableau de bord</span></a
          >
        </div>
        <!-- <div class="shrink-0 snap-center px-2">
          <a
            class="inline-flex items-center gap-2 rounded-full px-3 py-2 font-semibold text-default-600 transition-all hover:bg-default-100 hover:text-default-800 [&amp;.active]:bg-primary/10 [&amp;.active]:text-primary"
            :class="{ active: $route.path == '/dashboard/exercises' }"
            href="/dashboard/exercises"
          >
            <svg
              width="24px"
              height="24px"
              viewBox="0 0 36 36"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              aria-hidden="true"
              role="img"
              class="iconify iconify--twemoji"
              preserveAspectRatio="xMidYMid meet"
              fill="#000000"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
              <g id="SVGRepo_iconCarrier">
                <path
                  fill="#269"
                  d="M32 7H4a4 4 0 0 0-4 4v15a4 4 0 0 0 4 4h11.416c.52.596 1.477 1 2.584 1s2.065-.404 2.584-1H32a4 4 0 0 0 4-4V11a4 4 0 0 0-4-4z"
                ></path>
                <path fill="#292F33" d="M20 27a2 2 0 0 1-4 0V9a2 2 0 0 1 4 0v18z"></path>
                <path
                  fill="#99AAB5"
                  d="M18 26a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v18z"
                ></path>
                <path
                  fill="#E1E8ED"
                  d="M18 26c-.999-1.998-3.657-2-4-2c-2 0-5 2-8 2c-1 0-2-.896-2-2V8c0-1.104 1-2 2-2c3.255 0 6-2 8-2c3 0 4 1.896 4 3v19z"
                ></path>
                <path
                  fill="#99AAB5"
                  d="M34 26a2 2 0 0 1-2 2H20a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v18z"
                ></path>
                <path
                  fill="#CCD6DD"
                  d="M18 26c.999-1.998 3.657-2 4-2c2 0 5 2 8 2c1 0 2-.896 2-2V8c0-1.104-1-2-2-2c-3.256 0-6-2-8-2c-3 0-4 1.896-4 3v19z"
                ></path>
              </g>
            </svg>
            <span>Examens </span></a
          >
        </div> -->

        <div class="shrink-0 snap-center px-2">
          <a
            class="inline-flex items-center gap-2 rounded-full px-3 py-2 font-semibold text-default-600 transition-all hover:bg-default-100 hover:text-default-800 [&amp;.active]:bg-primary/10 [&amp;.active]:text-primary"
            href="/dashboard/history"
            :class="{ active: $route.path == '/dashboard/history' }"
          >
            <i class="fa-solid fa-refresh"></i>
            <span>Historique des tentatives</span></a
          >
        </div>
        <div class="shrink-0 snap-center px-2">
          <a
            class="inline-flex items-center gap-2 rounded-full px-3 py-2 font-semibold text-default-600 transition-all hover:bg-default-100 hover:text-default-800 [&amp;.active]:bg-primary/10 [&amp;.active]:text-primary"
            href="/dashboard/profile"
            :class="{ active: $route.path == '/dashboard/profile' }"
          >
            <svg
              width="24px"
              height="24px"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
              <g id="SVGRepo_iconCarrier">
                <circle cx="12" cy="6" r="4" fill="#1C274C"></circle>
                <ellipse opacity="0.5" cx="12" cy="17" rx="7" ry="4" fill="#1C274C"></ellipse>
              </g>
            </svg>
            <span>Mes informations</span></a
          >
        </div>

      </div>
    </nav>
  </header>
  <!--mobile menu-->
  <div id="mobile-menu" :class="{ open: isMobileMenuOpen, hidden: !isMobileMenuOpen }">
    <div
      class="absolute w-screen h-screen bg-black opacity-20 z-60 cursor-pointer"
      @click="toggleMobileMenu()"
    ></div>
    <div
      class="z-999 hs-overlay fixed bottom-0 left-0 top-0 z-[61] h-screen w-full max-w-[270px] -translate-x-full transform border-r border-gray-400 bg-white transition-all [--body-scroll:true] [--overlay-backdrop:false] hs-overlay-open:translate-x-0 dark:bg-default-50"
      aria-overlay="true"
      tabindex="-1"
    >
      <div
        class="flex h-[74px] items-center justify-between border-b border-gray-300 px-4 transition-all duration-300"
      >
        <a href="/"
          ><img src="@/assets/img/logo-long.png" alt="logo" class="flex h-10 dark:hidden" /><img
            src="@/assets/img/logo-long.png"
            alt="logo"
            class="hidden h-10 dark:flex"
        /></a>
        <div
          data-hs-overlay="#mobile-menu"
          class="hs-collapse-toggle cursor-pointer"
          @click="toggleMobileMenu()"
        >
          <svg
            stroke="currentColor"
            fill="none"
            stroke-width="2"
            viewBox="0 0 24 24"
            stroke-linecap="round"
            stroke-linejoin="round"
            height="24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </div>
      </div>
      <div class="h-[calc(100%-4rem)] overflow-y-auto">
        <nav class="hs-accordion-group flex h-full w-full flex-col flex-wrap p-4">
          <ul class="space-y-1">
            <li
              v-for="item in menuItems"
              :class="{ active: isLinkActive(item.url) }"
              class="rounded text-sm font-medium capitalize text-default-900 transition-all duration-300 hover:bg-default-100 hover:text-primary [&amp;.active]:bg-default-100 [&amp;.active]:text-primary"
            >
              <a class="block w-full px-4 py-2.5" :href="`${item.url}`">{{ item.name }}</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'
export default {
  name: 'app-header',
  data() {
    return {
      isMobileMenuOpen: false,
      menuItems: [
        {
          name: 'Accueil',
          url: '/',
        },
        {
          name: 'Examens ',
          url: '/dashboard/exercises',
        },
        {
          name: 'Méthodologie',
          url: '/methodologie',
        },
        {
          name: 'Packages',
          url: '/packages',
        },
      ],
      authStore: null,
      isLoggedIn: null,
      mobile_menu_is_open: false,
    }
  },
  methods: {
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
      if (this.isMobileMenuOpen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
    isLinkActive(url) {
      return this.$route.path === url
    },
    async logout() {
      await this.authStore.logout()
      this.$router.go('/login')
    },
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  },

  mounted() {
    this.authStore = useAuthStore()
    this.isLoggedIn = this.authStore.isAuthenticated
  },
}
</script>

<style scoped>
.active {
  color: #005ea5;
  background: #e0f4ff;
  border-radius: 10px;
}
</style>
